import React, { useEffect, useRef } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { Trophy, Star, TrendingUp, Target, RotateCcw, CheckCircle2, Sparkles, PartyPopper } from 'lucide-react';
import { ModuleId, Question, FamilyMasteryState } from '../../types';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { getMasteryColor, getMasteryBgColor, getMasteryLevel, getFamilyMasteryKey } from '../../utils/familyMasteryEngine';
import { getWeakestFamiliesForModule, getWeakestFamiliesGlobal } from '../../utils/masteryCanonical';
import { getFamilyDefinition } from '../../config/taxonomyRegistry';
import { useAppStore } from '../../hooks/useAppStore';
interface SessionSummaryProps {
  moduleId: ModuleId;
  sessionQuestions: Question[];
  sessionAnswers: {
    questionId: string;
    isCorrect: boolean;
  }[];
  preFamilyMastery: Record<string, FamilyMasteryState>;
  onExit: () => void;
  onPracticeAgain?: () => void;
  xpGained: number;
}
// Animated score counter hook
function useCountUp(target: number, duration = 1.4, delay = 0.6) {
  const motionVal = useMotionValue(0);
  const rounded = useTransform(motionVal, v => Math.round(v));
  useEffect(() => {
    const controls = animate(motionVal, target, {
      duration,
      delay,
      ease: 'easeOut'
    });
    return controls.stop;
  }, [target, duration, delay, motionVal]);
  return rounded;
}
// Confetti particle positions for high-score celebration
const CONFETTI = [{
  top: '8%',
  left: '12%',
  color: 'bg-yellow-400',
  size: 'w-3 h-3',
  delay: 0.4
}, {
  top: '15%',
  right: '10%',
  color: 'bg-emerald-400',
  size: 'w-2 h-2',
  delay: 0.55
}, {
  top: '25%',
  left: '6%',
  color: 'bg-indigo-400',
  size: 'w-2.5 h-2.5',
  delay: 0.3
}, {
  top: '30%',
  right: '8%',
  color: 'bg-rose-300',
  size: 'w-2 h-2',
  delay: 0.65
}, {
  top: '60%',
  left: '8%',
  color: 'bg-amber-300',
  size: 'w-3 h-3',
  delay: 0.5
}, {
  top: '70%',
  right: '6%',
  color: 'bg-sky-400',
  size: 'w-2 h-2',
  delay: 0.45
}];
export function SessionSummary({
  moduleId,
  sessionQuestions,
  sessionAnswers,
  preFamilyMastery,
  onExit,
  onPracticeAgain,
  xpGained
}: SessionSummaryProps) {
  const {
    state,
    selectedGroup
  } = useAppStore();
  const currentFamilyMastery = state.familyMastery || {};
  const correctCount = sessionAnswers.filter(a => a.isCorrect).length;
  const totalQuestions = sessionAnswers.length;
  const percentage = totalQuestions > 0 ? Math.round(correctCount / totalQuestions * 100) : 0;
  const isGreat = percentage >= 80;
  const isGood = percentage >= 50;
  // Animated score counter
  const animatedScore = useCountUp(percentage, 1.6, 0.7);
  const animatedCorrect = useCountUp(correctCount, 1.2, 0.8);
  const practicedFamilyIds = Array.from(new Set(sessionQuestions.map(q => q.familyId).filter((id): id is string => !!id)));
  const weakestFamilies = moduleId ? getWeakestFamiliesForModule(currentFamilyMastery, moduleId, selectedGroup, 1) : getWeakestFamiliesGlobal(currentFamilyMastery, selectedGroup, 1);
  const recommendation = weakestFamilies[0] ? {
    def: getFamilyDefinition(weakestFamilies[0].familyId),
    state: weakestFamilies[0]
  } : null;
  return <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-4 md:p-8 flex items-center justify-center" data-id="element-4826">
      <div className="max-w-2xl w-full" data-id="element-4827">
        <Card className="p-8 md:p-12 shadow-2xl shadow-indigo-100/60 border-slate-100 bg-white rounded-[2.5rem] relative overflow-hidden" data-id="element-4828">
          {/* Confetti particles for high scores */}
          {isGreat && CONFETTI.map((dot, i) => <motion.div key={i} className={`absolute rounded-full ${dot.color} ${dot.size} pointer-events-none`} style={{
          top: dot.top,
          left: dot.left,
          right: dot.right
        }} initial={{
          opacity: 0,
          scale: 0,
          y: 0
        }} animate={{
          opacity: [0, 1, 1, 0],
          scale: [0, 1.3, 1, 0.7],
          y: [0, -12, -20, -30]
        }} transition={{
          delay: dot.delay,
          duration: 2.5,
          ease: 'easeOut'
        }} data-id="element-4829" />)}

          {/* ── HEADER ── */}
          <div className="text-center mb-10 relative z-10" data-id="element-4830">
            {/* Trophy icon with spring entrance */}
            <motion.div initial={{
            scale: 0,
            rotate: -20
          }} animate={{
            scale: 1,
            rotate: 0
          }} transition={{
            type: 'spring',
            stiffness: 240,
            damping: 18,
            delay: 0.1
          }} className={`w-28 h-28 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl border-4 border-white relative ${isGreat ? 'bg-gradient-to-br from-yellow-300 via-amber-400 to-orange-400' : isGood ? 'bg-gradient-to-br from-indigo-400 to-indigo-600' : 'bg-gradient-to-br from-slate-300 to-slate-400'}`} data-id="element-4831">
              {isGreat ? <Trophy className="w-14 h-14 text-white drop-shadow-md" strokeWidth={2} data-id="element-4832" /> : isGood ? <Sparkles className="w-14 h-14 text-white drop-shadow-md" strokeWidth={2} data-id="element-4833" /> : <PartyPopper className="w-14 h-14 text-white drop-shadow-md" strokeWidth={2} data-id="element-4834" />}

              {/* Glow ring for great scores */}
              {isGreat && <motion.div className="absolute inset-0 rounded-full border-4 border-yellow-300/60" animate={{
              scale: [1, 1.15, 1],
              opacity: [0.8, 0, 0.8]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-4835" />}
            </motion.div>

            {/* Bold 'Sessie voltooid!' heading */}
            <motion.h1 initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3,
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} className="text-4xl md:text-5xl font-black text-slate-900 mb-2 tracking-tight" data-id="element-4836">
              Sessie voltooid! 🎓
            </motion.h1>

            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.45
          }} className={`text-xl font-bold mb-6 ${isGreat ? 'text-amber-600' : isGood ? 'text-indigo-600' : 'text-slate-500'}`} data-id="element-4837">
              {isGreat ? 'Fantastisch gedaan! 🌟' : isGood ? 'Goed bezig! 💪' : 'Goed geoefend! 📚'}
            </motion.p>

            {/* Score reveal — animated counter */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.5,
            type: 'spring',
            stiffness: 260,
            damping: 20
          }} className="flex items-center justify-center gap-4 flex-wrap" data-id="element-4838">
              {/* Big score circle */}
              <div className={`relative w-24 h-24 rounded-full flex items-center justify-center shadow-lg border-4 ${isGreat ? 'bg-emerald-500 border-emerald-300' : isGood ? 'bg-indigo-500 border-indigo-300' : 'bg-slate-400 border-slate-300'}`} data-id="element-4839">
                <motion.span className="text-2xl font-black text-white" data-id="element-4840">
                  {animatedScore}
                </motion.span>
                <span className="text-sm font-black text-white/80 absolute bottom-3" data-id="element-4841">
                  %
                </span>
              </div>

              <div className="flex flex-col gap-2" data-id="element-4842">
                <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-full border border-slate-100 shadow-sm" data-id="element-4843">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-4844" />
                  <span className="font-bold text-slate-700 text-sm" data-id="element-4845">
                    <motion.span data-id="element-4846">{animatedCorrect}</motion.span> van de{' '}
                    {totalQuestions} goed
                  </span>
                </div>

                <div className="flex items-center gap-2 bg-amber-50 px-4 py-2 rounded-full border border-amber-100 shadow-sm" data-id="element-4847">
                  <Star className="w-4 h-4 fill-amber-400 text-amber-400 flex-shrink-0" data-id="element-4848" />
                  <span className="font-black text-amber-700 text-sm" data-id="element-4849">
                    +{xpGained} XP verdiend
                  </span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* ── MASTERY GROWTH ── */}
          {practicedFamilyIds.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6,
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1]
        }} className="mb-8" data-id="element-4850">
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2 px-1" data-id="element-4851">
                <TrendingUp className="w-4 h-4" data-id="element-4852" />
                Jouw groei deze sessie
              </h3>
              <div className="space-y-3" data-id="element-4853">
                {practicedFamilyIds.map((familyId, index) => {
              const def = getFamilyDefinition(familyId);
              const masteryKey = getFamilyMasteryKey(familyId, selectedGroup);
              const pre = preFamilyMastery[masteryKey]?.masteryScore || 0;
              const post = currentFamilyMastery[masteryKey]?.masteryScore || 0;
              const delta = post - pre;
              if (!def) return null;
              return <motion.div key={familyId} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.7 + index * 0.1,
                duration: 0.4
              }} className="bg-slate-50 rounded-2xl p-4 border border-slate-100" data-id="element-4854">
                      <div className="flex justify-between items-center mb-2.5" data-id="element-4855">
                        <div className="font-bold text-slate-800 text-sm" data-id="element-4856">
                          {def.description}
                        </div>
                        <div className="flex items-center gap-2 text-xs font-black" data-id="element-4857">
                          <span className={delta > 0 ? 'text-emerald-600' : 'text-slate-400'} data-id="element-4858">
                            {delta > 0 ? `+${Math.round(delta)}%` : '—'}
                          </span>
                          <span className="text-slate-500 font-bold" data-id="element-4859">
                            {Math.round(Math.max(0, Math.min(100, post)))}%
                          </span>
                        </div>
                      </div>

                      <div className="h-3 bg-slate-200 rounded-full overflow-hidden relative" data-id="element-4860">
                        {/* Pre bar */}
                        <div className="absolute top-0 left-0 h-full bg-slate-300/60 rounded-full" style={{
                    width: `${Math.max(0, Math.min(100, pre))}%`
                  }} data-id="element-4861" />
                        {/* Post bar — animated */}
                        <motion.div initial={{
                    width: `${Math.max(0, Math.min(100, pre))}%`
                  }} animate={{
                    width: `${Math.max(0, Math.min(100, post))}%`
                  }} transition={{
                    duration: 1.6,
                    ease: 'easeOut',
                    delay: 0.8 + index * 0.1
                  }} className={`absolute top-0 left-0 h-full rounded-full ${post >= 85 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' : post >= 55 ? 'bg-gradient-to-r from-amber-400 to-amber-500' : 'bg-gradient-to-r from-indigo-400 to-indigo-500'}`} data-id="element-4862" />
                      </div>
                    </motion.div>;
            })}
              </div>
            </motion.div>}

          {/* ── RECOMMENDATION ── */}
          {recommendation && recommendation.def && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.85,
          duration: 0.45
        }} className="mb-8" data-id="element-4863">
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2 px-1" data-id="element-4864">
                <Target className="w-4 h-4" data-id="element-4865" />
                Volgende keer focussen op
              </h3>
              <div className="bg-indigo-50 border-2 border-indigo-100 rounded-3xl p-5 flex items-center gap-5 shadow-sm" data-id="element-4866">
                <div className="bg-white p-3 rounded-2xl shadow-sm text-indigo-600 border border-indigo-100 flex-shrink-0" data-id="element-4867">
                  <RotateCcw className="w-6 h-6" data-id="element-4868" />
                </div>
                <div data-id="element-4869">
                  <div className="font-black text-slate-900 text-lg mb-1" data-id="element-4870">
                    {recommendation.def.description}
                  </div>
                  <div className="text-sm text-slate-600 font-medium flex items-center gap-2 flex-wrap" data-id="element-4871">
                    <span className={`px-2 py-0.5 rounded-md text-xs font-bold uppercase tracking-wide ${getMasteryBgColor(recommendation.state.masteryScore)} ${getMasteryColor(recommendation.state.masteryScore)}`} data-id="element-4872">
                      {getMasteryLevel(recommendation.state.masteryScore)}
                    </span>
                    <span data-id="element-4873">•</span>
                    <span data-id="element-4874">
                      Score:{' '}
                      {Math.round(Math.max(0, Math.min(100, recommendation.state.masteryScore)))}
                      %
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>}

          {/* ── ACTION BUTTONS ── */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.0,
          duration: 0.45
        }} className="relative z-20 space-y-3" data-id="element-4875">
            {onPracticeAgain && <Button onClick={onPracticeAgain} size="xl" variant="primary" fullWidth className="shadow-xl shadow-indigo-200 h-16 text-lg rounded-2xl" data-id="element-4876">
                <RotateCcw className="w-5 h-5 mr-2" data-id="element-4877" />
                Nog een keer oefenen
              </Button>}
            <Button onClick={onExit} size="xl" variant={onPracticeAgain ? 'secondary' : 'primary'} fullWidth className={`h-16 text-lg rounded-2xl ${onPracticeAgain ? '' : 'shadow-xl shadow-indigo-200'}`} data-id="element-4878">
              Terug naar overzicht
            </Button>
          </motion.div>
        </Card>
      </div>
    </div>;
}